import type { CSSProperties } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  ASSET_INTERACTION_CLASS,
  AUDIO_FALLBACK_IMAGE_16X9_SRC,
  AUDIO_FALLBACK_IMAGE_1X1_SRC,
  MAX_VOLUME,
  MINI_PROGRESS_WIDTH_LARGE_DESKTOP,
  MINI_SIDE_CONTAINER_WIDTH_LARGE_DESKTOP,
  MIN_VOLUME,
  PLAYBACK_CONTROL_BUTTON_CLASS,
  PLAYBACK_CONTROL_SIZE_DESKTOP,
  VOLUME_STEP,
} from './audio-player-constants';
import { clamp, hasVisibleText } from './audio-player-utils';
import { IconButton } from './icon-button';
import { PlaybackSpeed } from './playback-speed';
import { ProgressCluster } from './progress-cluster';
import type { MiniAudioPlayerProps } from './types';

type MiniPlayerArtistLineProps = {
  artist: string | null;
  artistColor: string;
  artistTextStyle: CSSProperties;
};

/**
 * Renders the mini-player artist line when the label has visible text.
 * A null or whitespace-only label is omitted.
 */
function MiniPlayerArtistLine({
  artist,
  artistColor,
  artistTextStyle,
}: MiniPlayerArtistLineProps): JSX.Element | null {
  if (!hasVisibleText(artist)) return null;

  return (
    <p className="truncate" style={{ color: artistColor, ...artistTextStyle }}>
      {artist}
    </p>
  );
}

/** Renders the compact player variants for desktop and mobile layouts. */
export function MiniAudioPlayer({
  entry,
  title,
  artist,
  imageSrc,
  hasQueue,
  hasSpeedControl,
  playbackRate,
  speedOptions,
  isQueueOpen,
  isMuted,
  volume,
  isPlaying,
  currentTime,
  duration,
  remaining,
  containerClassName,
  titleColor,
  artistColor,
  titleTextStyle,
  artistTextStyle,
  timeLabelTextStyle,
  imageCornerRadius,
  imagePlaceholderColor,
  imageBorderColor,
  progressColor,
  totalBarColor,
  handleColor,
  elapsedLabelColor,
  totalLabelColor,
  miniProgressWidth,
  miniSideContainerWidth,
  showFavoriteDesktop,
  showAddDesktop,
  isFavorited,
  isPlaylistAdded,
  iconAssets,
  formatTime,
  onSeek,
  onExpand,
  onPrevious,
  onTogglePlayback,
  onNext,
  onSkipBack,
  onSkipAhead,
  onToggleMute,
  onVolumeChange,
  onToggleQueue,
  onSelectPlaybackRate,
  onClose,
  onToggleFavorite,
  onToggleAddToPlaylist,
  onAddToPlaylistPressStart,
  addToPlaylistButtonRef,
  playbackSpeedOpen,
  setPlaybackSpeedOpen,
  children,
}: MiniAudioPlayerProps): JSX.Element {
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const volumePercent = clamp((isMuted ? 0 : volume) * 100, 0, 100);
  const resolvedMiniProgressWidth =
    typeof miniProgressWidth === 'number'
      ? `${miniProgressWidth}px`
      : miniProgressWidth;
  const desktopMiniArtworkSrc = imageSrc || AUDIO_FALLBACK_IMAGE_1X1_SRC;
  const mobileMiniArtworkSrc = imageSrc || AUDIO_FALLBACK_IMAGE_16X9_SRC;

  return (
    <>
      {children}

      <div
        data-mini-audio-player
        className={twMerge(
          'pointer-events-auto relative z-[9999] mx-auto mb-2 hidden w-[calc(100%-16px)] cursor-pointer items-center justify-between gap-6 border px-4 py-3 text-[#efefef] shadow-2xl shadow-black/40 desktop:flex',
          containerClassName
        )}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('button')) return;
          if ((e.target as HTMLElement).closest('input')) return;

          onExpand();
        }}
      >
        <div
          className="relative z-[1] flex min-w-0 shrink-0 items-center justify-start gap-[16px] large-desktop:w-[var(--mini-side-container-width-large-desktop)]"
          style={
            {
              width: miniSideContainerWidth,
              '--mini-side-container-width-large-desktop':
                MINI_SIDE_CONTAINER_WIDTH_LARGE_DESKTOP,
            } as CSSProperties
          }
        >
          <div
            data-testid="mini-desktop-artwork"
            className="relative h-14 w-14 shrink-0 overflow-hidden"
            style={{
              borderRadius: imageCornerRadius,
              backgroundColor: imagePlaceholderColor,
            }}
          >
            <img
              data-testid="mini-desktop-artwork-image"
              src={desktopMiniArtworkSrc}
              alt={entry.title}
              className="h-full w-full object-cover"
            />
            <span
              aria-hidden
              data-testid="mini-desktop-artwork-border"
              className="pointer-events-none absolute inset-0"
              style={{
                borderRadius: imageCornerRadius,
                boxShadow: `inset 0 0 0 1px ${imageBorderColor}`,
              }}
            />
          </div>
          <div className="audio-player-mini_text_label_gutter flex min-w-0 flex-1 flex-col">
            {hasVisibleText(title) ? (
              <p
                className="truncate"
                style={{ color: titleColor, ...titleTextStyle }}
              >
                {title}
              </p>
            ) : null}
            <MiniPlayerArtistLine
              artist={artist}
              artistColor={artistColor}
              artistTextStyle={artistTextStyle}
            />
          </div>

          {showFavoriteDesktop || showAddDesktop ? (
            <div className="ml-auto flex shrink-0 items-center gap-[4px]">
              {showFavoriteDesktop ? (
                <IconButton
                  src={isFavorited ? iconAssets.favorited : iconAssets.favorite}
                  alt={isFavorited ? 'Unfavorite' : 'Favorite'}
                  onClick={onToggleFavorite}
                  size={20}
                  iconClassName={`audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
                  iconColor={'var(--action_button_asset_color_default)'}
                  buttonClassName={`group flex h-[40px] w-[40px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active`}
                />
              ) : null}
              {showAddDesktop ? (
                <IconButton
                  buttonRef={addToPlaylistButtonRef}
                  src={
                    isPlaylistAdded
                      ? iconAssets.addedToPlaylist
                      : iconAssets.addToPlaylist
                  }
                  alt={
                    isPlaylistAdded ? 'Added to playlist' : 'Add to playlist'
                  }
                  onClick={onToggleAddToPlaylist}
                  onPointerDown={onAddToPlaylistPressStart}
                  onMouseDown={onAddToPlaylistPressStart}
                  size={20}
                  iconClassName={`audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
                  iconColor={'var(--action_button_asset_color_default)'}
                  buttonClassName={`group flex h-[40px] w-[40px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active`}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        <div
          className="absolute left-1/2 top-1/2 flex w-[var(--mini-progress-cluster-width-desktop)] -translate-x-1/2 -translate-y-1/2 items-center justify-center large-desktop:w-[var(--mini-progress-cluster-width-large-desktop)]"
          style={
            {
              '--mini-progress-cluster-width-desktop': `calc(${resolvedMiniProgressWidth} + 136px)`,
              '--mini-progress-cluster-width-large-desktop': `calc(${MINI_PROGRESS_WIDTH_LARGE_DESKTOP} + 136px)`,
            } as CSSProperties
          }
        >
          <div className="flex w-full flex-col items-center gap-2">
            <div className="flex items-center justify-center gap-2">
              <IconButton
                src={iconAssets.previous}
                alt="Previous"
                onClick={onPrevious}
                size={PLAYBACK_CONTROL_SIZE_DESKTOP.skipTrack}
                iconClassName={ASSET_INTERACTION_CLASS}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />
              <IconButton
                src={iconAssets.skipBack}
                alt="Rewind"
                onClick={onSkipBack}
                size={PLAYBACK_CONTROL_SIZE_DESKTOP.seek}
                iconClassName={ASSET_INTERACTION_CLASS}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />

              <IconButton
                src={isPlaying ? iconAssets.pause : iconAssets.play}
                alt={isPlaying ? 'Pause' : 'Play'}
                onClick={onTogglePlayback}
                size={PLAYBACK_CONTROL_SIZE_DESKTOP.playPause}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconClassName={ASSET_INTERACTION_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />

              <IconButton
                src={iconAssets.skipAhead}
                alt="Forward"
                onClick={onSkipAhead}
                size={PLAYBACK_CONTROL_SIZE_DESKTOP.seek}
                iconClassName={ASSET_INTERACTION_CLASS}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />
              <IconButton
                src={iconAssets.next}
                alt="Next"
                onClick={onNext}
                size={PLAYBACK_CONTROL_SIZE_DESKTOP.skipTrack}
                iconClassName={ASSET_INTERACTION_CLASS}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />
            </div>

            <ProgressCluster
              width={miniProgressWidth}
              largeDesktopWidth={MINI_PROGRESS_WIDTH_LARGE_DESKTOP}
              currentTime={currentTime}
              duration={duration}
              remaining={remaining}
              progressColor={progressColor}
              totalBarColor={totalBarColor}
              handleColor={handleColor}
              elapsedLabelColor={elapsedLabelColor}
              totalLabelColor={totalLabelColor}
              timeLabelTextStyle={timeLabelTextStyle}
              formatTime={formatTime}
              onSeek={onSeek}
            />
          </div>
        </div>
        <div
          className="relative z-[1] flex min-w-0 shrink-0 items-center justify-end gap-[4px] large-desktop:w-[var(--mini-side-container-width-large-desktop)]"
          style={
            {
              width: miniSideContainerWidth,
              '--mini-side-container-width-large-desktop':
                MINI_SIDE_CONTAINER_WIDTH_LARGE_DESKTOP,
            } as CSSProperties
          }
        >
          {hasSpeedControl ? (
            <PlaybackSpeed
              playbackRate={playbackRate}
              speedOptions={speedOptions}
              playbackSpeedOpen={playbackSpeedOpen}
              setPlaybackSpeedOpen={setPlaybackSpeedOpen}
              onSelectPlaybackRate={onSelectPlaybackRate}
              iconAssets={iconAssets}
            />
          ) : null}

          <IconButton
            src={iconAssets.queue}
            alt={isQueueOpen ? 'Hide queue' : 'Show queue'}
            onClick={onToggleQueue}
            size={20}
            iconColor={'var(--action_button_asset_color_default)'}
            buttonClassName={`group flex h-[40px] w-[40px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active`}
            iconClassName={`h-[20px] w-[20px] audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
          />

          <IconButton
            src={isMuted ? iconAssets.muted : iconAssets.volume}
            alt={isMuted ? 'Unmute' : 'Mute'}
            onClick={onToggleMute}
            size={20}
            iconClassName={`audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
            iconColor={'var(--action_button_asset_color_default)'}
            buttonClassName={`group flex h-[40px] w-[40px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active`}
          />
          <div
            className="group/volume relative ml-1 mr-2 flex items-center"
            style={{ width: 80 }}
          >
            <div
              data-testid="mini-volume-track"
              className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
              style={{ backgroundColor: totalBarColor }}
            />
            <div
              data-testid="mini-volume-fill"
              className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
              style={{
                width: `${volumePercent}%`,
                backgroundColor: progressColor,
              }}
            />
            <input
              type="range"
              min={MIN_VOLUME}
              max={MAX_VOLUME}
              step={VOLUME_STEP}
              value={isMuted ? 0 : volume}
              onChange={onVolumeChange}
              className="relative h-5 w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:opacity-0 [&::-webkit-slider-thumb]:opacity-0"
              aria-label="Volume"
            />
            <span
              data-testid="mini-volume-handle"
              className="pointer-events-none absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 transition-opacity duration-150 group-hover/volume:opacity-100"
              style={{
                left: `${volumePercent}%`,
                backgroundColor: handleColor,
              }}
            />
          </div>
        </div>
      </div>

      <div
        data-mini-audio-player
        className={twMerge(
          'pointer-events-auto relative mx-auto mb-2 flex w-[calc(100%-16px)] items-center justify-between gap-3 overflow-hidden border border-b-0 p-3 text-[#efefef] shadow-2xl shadow-black/40 desktop:hidden',
          containerClassName
        )}
      >
        <button
          type="button"
          onClick={onExpand}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-label="Expand player"
        >
          <div
            data-testid="mini-mobile-artwork"
            className="h-14 w-14 shrink-0 overflow-hidden border"
            style={{
              borderRadius: imageCornerRadius,
              backgroundColor: imagePlaceholderColor,
              borderColor: imageBorderColor,
            }}
          >
            <img
              data-testid="mini-mobile-artwork-image"
              src={mobileMiniArtworkSrc}
              alt={entry.title}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="audio-player-mini_text_label_gutter flex min-w-0 flex-1 flex-col">
            {hasVisibleText(title) ? (
              <p
                className="truncate"
                style={{ color: titleColor, ...titleTextStyle }}
              >
                {title}
              </p>
            ) : null}
            <MiniPlayerArtistLine
              artist={artist}
              artistColor={artistColor}
              artistTextStyle={artistTextStyle}
            />
          </div>
        </button>

        <IconButton
          src={isPlaying ? iconAssets.pause : iconAssets.play}
          alt={isPlaying ? 'Pause' : 'Play'}
          onClick={onTogglePlayback}
          size={48}
          buttonClassName="group relative z-[1] flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
          iconClassName={`h-12 w-12 ${ASSET_INTERACTION_CLASS}`}
          iconColor={'white'}
          preserveSvgColor={true}
        />

        <div
          data-testid="mini-mobile-progress-track"
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-1"
          style={{ backgroundColor: totalBarColor }}
        >
          <div
            data-testid="mini-mobile-progress-fill"
            className="h-full"
            style={{
              width: `${clamp(progressPercent, 0, 100)}%`,
              backgroundColor: progressColor,
            }}
          />
        </div>
        <input
          data-testid="mini-mobile-progress-seek"
          type="range"
          min={0}
          max={Number.isFinite(duration) && duration > 0 ? duration : 100}
          step={0.5}
          value={currentTime}
          onChange={onSeek}
          className="absolute bottom-0 left-0 right-0 z-[2] m-0 h-3 w-full cursor-pointer appearance-none opacity-0"
          aria-label="Seek"
        />
      </div>
    </>
  );
}
